<template>
  <div>
    <b-overlay :show="formShow" rounded="sm" no-fade>
      <b-card class="m-0 p-0">
        <!-- ─── Header ─── -->
        <b-row class="mb-25">
          <b-col cols="12" class="mb-1 mb-md-0">
            <div class="date-container d-flex flex-column">
              <b-row class="d-flex align-items-center justify-content-between">
                <b-col cols="12" md="4" class="d-flex align-items-center">
                  <h4 class="mb-0">Risk Management Tool</h4>
                  <b-button
                    v-if="$Can('show_risk_report_menu')"
                    id="risk-report-control"
                    variant="primary"
                    size="sm"
                    class="ml-1"
                  >
                    <span class="d-flex align-items-center">
                      Reports
                      <feather-icon icon="BarChart2Icon" style="margin-left:0.3rem;" />
                    </span>
                  </b-button>
                  <b-popover
                    target="risk-report-control"
                    triggers="click blur"
                    variant="primary"
                    custom-class="report-popover-custom"
                  >
                    <template #title>Report Control</template>
                    <div class="report-menu-container">
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        @click="showReportModal('regular')"
                      >
                        <span class="trades-font">
                          Risk Register Report
                          <feather-icon icon="FileTextIcon" style="margin-left:0.3rem;" />
                        </span>
                      </b-button>
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        @click="showReportModal('summary')"
                      >
                        <span class="trades-font">
                          Overall Rating Summary Report
                          <feather-icon icon="PieChartIcon" style="margin-left:0.3rem;" />
                        </span>
                      </b-button>
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        @click="showReportModal('live')"
                      >
                        <span class="trades-font">
                          Live Risks Requiring Attention
                          <feather-icon icon="AlertTriangleIcon" style="margin-left:0.3rem;" />
                        </span>
                      </b-button>
                      <b-button
                        variant="outline-secondary"
                        size="sm"
                        @click="showReportModal('archived')"
                      >
                        <span class="trades-font">
                          Archived Risk Register Report
                          <feather-icon icon="ArchiveIcon" style="margin-left:0.3rem;" />
                        </span>
                      </b-button>
                    </div>
                  </b-popover>
                </b-col>

                <b-col
                  v-if="$Can('risk_tool_filter_options')"
                  cols="12"
                  md="8"
                  class="d-flex align-items-center justify-content-end flex-wrap"
                >
                  <v-select
                    v-model="selectedYear"
                    :options="yearOptions"
                    placeholder="Select Year"
                    :clearable="false"
                    :searchable="false"
                    :appendToBody="true"
                    class="small-select ml-2"
                    style="min-width:130px;"
                    @input="loadRiskItems"
                  />
                  <!-- <b-form-checkbox
                    v-model="showPreviousYear"
                    switch
                    size="sm"
                    class="custom-switch ml-2"
                    @change="onPreviousYearChange"
                  >
                    <span class="switch-label ml-0 pl-0">Include unfinished from previous years</span>
                  </b-form-checkbox> -->
                  <b-form-checkbox
                    v-model="showArchived"
                    switch
                    size="sm"
                    class="custom-switch ml-2"
                    @change="onShowArchivedChange"
                  >
                    <span class="switch-label ml-0 pl-0">Show Archived</span>
                  </b-form-checkbox>
                </b-col>
              </b-row>
            </div>
          </b-col>
        </b-row>

        <!-- ─── Table ─── -->
        <div
          class="table-responsive mt-2"
          style="min-height:320px; height:75vh; overflow-y:auto;"
          v-if="$Can('show_risk_tool_table')"
        >
          <table class="table table-bordered risk-tool-table" :class="{ 'with-far': isAdmin }" v-if="groupedRows.length > 0">
            <thead>
              <tr style="font-size:0.6rem !important;">
                <th v-if="isAdmin" style="width:55px; min-width:55px;" class="sticky-col sticky-far">FAR</th>
                <th style="width:55px; min-width:55px;" class="sticky-col sticky-no">Risk No.</th>
                <th class="risk-col sticky-col sticky-risk">Risk</th>
                <th class="impact-col">Potential Impact</th>
                <th style="width:50px; min-width:50px; text-align:center;">
                  Likelihood of Occurrence<br>1–5
                </th>
                <th style="width:50px; min-width:50px; text-align:center;">
                  Consequence for CIS<br>1–5
                </th>
                <th style="width:65px; min-width:65px; text-align:center;" class="rating-header">
                  Inherent Risk Rating
                </th>
                <th class="mitigation-col">Mitigation Actions in Place</th>
                <th style="width:50px; min-width:50px; text-align:center;">
                  Treated Likelihood<br>1–5
                </th>
                <th style="width:50px; min-width:50px; text-align:center;">
                  Treated Consequence<br>1–5
                </th>
                <th style="width:65px; min-width:65px; text-align:center;" class="rating-header">
                  Residual Risk Rating
                </th>
                <th style="width:120px; min-width:120px;">Owner</th>
                <th style="width:160px; min-width:160px;">Future Recommended Actions</th>
                <th style="width:110px; min-width:110px;">Target Completion Date</th>
                <th style="width:55px; min-width:55px; background-color:#008bcc !important;">#</th>
              </tr>
            </thead>

            <template v-for="(categoryGroup, categoryIndex) in groupedRows">
              <!-- Category Header Row -->
              <tr :key="'cat-' + categoryIndex">
                <td
                  :colspan="isAdmin ? 17 : 16"
                  class="pillar-cell"
                  @click="toggleSelect($event, 'category', categoryIndex)"
                  :class="{ editing: isEditing(categoryIndex, 'category') }"
                  :style="!isEditing(categoryIndex, 'category') && categoryGroup.color ? 'background-color:' + categoryGroup.color : ''"
                >
                  <div class="d-flex align-items-center justify-content-between w-100">
                    <template v-if="isEditing(categoryIndex, 'category')">
                      <v-select
                        v-model="categoryGroup.risk_category_id"
                        placeholder="Select Risk Category"
                        :options="getFilteredCategoryOptions()"
                        label="title"
                        :reduce="(val) => val.id"
                        class="fixed-select pillar-select"
                        :appendToBody="true"
                        @blur="hideSelect($event, 'category', categoryIndex)"
                        @input="onCategoryChange($event, categoryIndex)"
                        @option:created="onCreateNewField($event, 'risk_category', null, categoryIndex)"
                        :taggable="true"
                        :selectable="(option) => !option.disabled"
                        :loading="isOnSelectChangeLoading"
                      >
                        <template #option="option">
                          <span :title="option.title">{{ option.title }}</span>
                        </template>
                      </v-select>
                    </template>
                    <template v-else>
                      <div class="d-flex align-items-center flex-grow-1">
                        <span class="cell-text pillar-text">
                          {{
                            categoryGroup.risk_category_id
                              ? "Risk-" + categoryGroup.order + ": " + getCategoryTitle(categoryGroup.risk_category_id)
                              : "Select Risk Category"
                          }}
                        </span>
                        <div class="pillar-action-column">
                          <b-button
                            v-if="$Can('delete_tool_item') && (!categoryGroup.rows || categoryGroup.rows.length === 0)"
                            variant="outline-danger"
                            size="sm"
                            @click.stop="deleteCategory(categoryIndex)"
                            class="delete-pillar-btn"
                          >
                            Delete Category <i class="fas fa-times ml-1"></i>
                          </b-button>
                        </div>
                      </div>
                    </template>
                  </div>
                </td>
              </tr>

              <!-- Draggable rows for this category -->
              <draggable
                :list="categoryGroup.rows"
                tag="tbody"
                @end="onDragEnd($event, categoryIndex)"
                handle=".drag-handle"
                :animation="200"
                ghost-class="ghost-row"
                drag-class="drag-row"
                :key="'drag-' + categoryIndex"
              >
                <tr
                  v-for="(row, rowIndex) in categoryGroup.rows"
                  :key="'row-' + categoryIndex + '-' + rowIndex"
                  :class="{ 'far-report-selected': row.far_report, 'archived-row': row.is_archived }"
                >
                  <!-- FAR Checkbox -->
                  <td
                    v-if="isAdmin"
                    style="width:55px; min-width:55px; max-width:55px; text-align:center;"
                    class="far-cell sticky-col sticky-far"
                  >
                    <div class="cib-checkbox-container">
                      <span v-if="row.far_report" class="cib-text">FAR</span>
                      <b-form-checkbox
                        v-model="row.far_report"
                        class="custom-control-primary mx-auto"
                        :class="{ 'hover-visible': row.far_report }"
                        @change="onFarReportChange(row)"
                      />
                    </div>
                  </td>

                  <!-- Risk Number -->
                  <td style="text-align:center; font-weight:bold; color:#555; font-size:0.75rem;" class="sticky-col sticky-no">
                    {{ categoryGroup.order }}.{{ rowIndex + 1 }}
                  </td>

                  <!-- Risk (Objective) -->
                  <td
                    @click="toggleSelect($event, 'risk_objective', categoryIndex + '-' + rowIndex)"
                    :class="{
                      editing: isEditing(categoryIndex + '-' + rowIndex, 'risk_objective'),
                      'objective-span-cell': true,
                      'sticky-col': true,
                      'sticky-risk': true
                    }"
                  >
                    <template v-if="isEditing(categoryIndex + '-' + rowIndex, 'risk_objective')">
                      <v-select
                        v-model="row.risk_objective_id"
                        :options="getFilteredObjectives(categoryGroup.risk_category_id, row.risk_objective_id)"
                        placeholder="Select or type risk..."
                        label="title"
                        :reduce="(val) => val.id"
                        :clearable="true"
                        class="fixed-select objective-select"
                        :appendToBody="true"
                        :taggable="true"
                        @option:created="onCreateNewField($event, 'risk_objective', categoryGroup.risk_category_id, categoryIndex + '-' + rowIndex)"
                        @blur="hideSelect($event, 'risk_objective', categoryIndex + '-' + rowIndex)"
                        @input="onFieldChange(row, 'risk_objective_id', $event)"
                      >
                        <template #option="option">
                          <span :title="option.title">{{ option.title }}</span>
                        </template>
                      </v-select>
                    </template>
                    <template v-else>
                      <span
                        :id="`popover-risk-${categoryIndex}-${rowIndex}`"
                        class="cell-text"
                      >{{ getOptionTitle(row.risk_objective_id, 'risk_objective_id') || '—' }}</span>
                      <b-popover
                        v-if="row.risk_objective_id"
                        :target="`popover-risk-${categoryIndex}-${rowIndex}`"
                        triggers="hover"
                        :delay="1000"
                      >
                        <template #title><span>Risk</span></template>
                        <span>{{ getOptionTitle(row.risk_objective_id, 'risk_objective_id') }}</span>
                      </b-popover>
                    </template>
                  </td>

                  <!-- Potential Impact -->
                  <td
                    @click="toggleSelect($event, 'risk_impact', categoryIndex + '-' + rowIndex)"
                    :class="{ editing: isEditing(categoryIndex + '-' + rowIndex, 'risk_impact') }"
                  >
                    <template v-if="isEditing(categoryIndex + '-' + rowIndex, 'risk_impact')">
                      <v-select
                        v-model="row.risk_impact_id"
                        :options="getFilteredImpacts(row.risk_objective_id, row.risk_impact_id)"
                        placeholder="Select or type impact..."
                        label="title"
                        :reduce="(val) => val.id"
                        :clearable="true"
                        class="fixed-select"
                        :appendToBody="true"
                        :taggable="true"
                        @option:created="onCreateNewField($event, 'risk_impact', row.risk_objective_id, categoryIndex + '-' + rowIndex)"
                        @blur="hideSelect($event, 'risk_impact', categoryIndex + '-' + rowIndex)"
                        @input="onFieldChange(row, 'risk_impact_id', $event)"
                      >
                        <template #option="option">
                          <span :title="option.title">{{ option.title }}</span>
                        </template>
                      </v-select>
                    </template>
                    <template v-else>
                      <span
                        :id="`popover-impact-${categoryIndex}-${rowIndex}`"
                        class="cell-text"
                      >{{ getOptionTitle(row.risk_impact_id, 'risk_impact_id') || '—' }}</span>
                      <b-popover
                        v-if="row.risk_impact_id"
                        :target="`popover-impact-${categoryIndex}-${rowIndex}`"
                        triggers="hover"
                        :delay="1000"
                      >
                        <template #title><span>Potential Impact</span></template>
                        <span>{{ getOptionTitle(row.risk_impact_id, 'risk_impact_id') }}</span>
                      </b-popover>
                    </template>
                  </td>

                  <!-- Likelihood -->
                  <td
                    style="text-align:center;"
                    @click="toggleSelect($event, 'likelihood', categoryIndex + '-' + rowIndex)"
                    :class="{ editing: isEditing(categoryIndex + '-' + rowIndex, 'likelihood') }"
                  >
                    <template v-if="isEditing(categoryIndex + '-' + rowIndex, 'likelihood')">
                      <v-select
                        v-model="row.likelihood"
                        :options="[1,2,3,4,5]"
                        placeholder="1–5"
                        :clearable="true"
                        :searchable="false"
                        :appendToBody="true"
                        class="fixed-select small-number-select"
                        @blur="hideSelect($event, 'likelihood', categoryIndex + '-' + rowIndex)"
                        @input="onFieldChange(row, 'likelihood', $event)"
                      />
                    </template>
                    <template v-else>
                      <span class="cell-text">{{ row.likelihood || '—' }}</span>
                    </template>
                  </td>

                  <!-- Consequence -->
                  <td
                    style="text-align:center;"
                    @click="toggleSelect($event, 'consequence', categoryIndex + '-' + rowIndex)"
                    :class="{ editing: isEditing(categoryIndex + '-' + rowIndex, 'consequence') }"
                  >
                    <template v-if="isEditing(categoryIndex + '-' + rowIndex, 'consequence')">
                      <v-select
                        v-model="row.consequence"
                        :options="[1,2,3,4,5]"
                        placeholder="1–5"
                        :clearable="true"
                        :searchable="false"
                        :appendToBody="true"
                        class="fixed-select small-number-select"
                        @blur="hideSelect($event, 'consequence', categoryIndex + '-' + rowIndex)"
                        @input="onFieldChange(row, 'consequence', $event)"
                      />
                    </template>
                    <template v-else>
                      <span class="cell-text">{{ row.consequence || '—' }}</span>
                    </template>
                  </td>

                  <!-- Inherent Risk Rating (calculated) -->
                  <td
                    class="rating-td"
                    :class="getRatingClass(calcRating(row.likelihood, row.consequence))"
                    style="text-align:center; font-weight:bold;"
                  >
                    <span v-if="row.likelihood && row.consequence">
                      {{ calcRating(row.likelihood, row.consequence) }}
                    </span>
                    <span v-else style="color:#aaa;">—</span>
                  </td>

                  <!-- Mitigation Actions -->
                  <td
                    @click="toggleSelect($event, 'mitigation_action', categoryIndex + '-' + rowIndex)"
                    :class="{ editing: isEditing(categoryIndex + '-' + rowIndex, 'mitigation_action') }"
                  >
                    <template v-if="isEditing(categoryIndex + '-' + rowIndex, 'mitigation_action')">
                      <v-select
                        v-model="row.risk_mitigation_action_id"
                        :options="mitigationActionOptions"
                        placeholder="Select or type mitigation action..."
                        label="title"
                        :reduce="(val) => val.id"
                        :clearable="true"
                        class="fixed-select"
                        :appendToBody="true"
                        :taggable="true"
                        @option:created="onCreateNewField($event, 'risk_mitigation_action', null, categoryIndex + '-' + rowIndex)"
                        @blur="hideSelect($event, 'mitigation_action', categoryIndex + '-' + rowIndex)"
                        @input="onFieldChange(row, 'risk_mitigation_action_id', $event)"
                      >
                        <template #option="option">
                          <span :title="option.title">{{ option.title }}</span>
                        </template>
                      </v-select>
                    </template>
                    <template v-else>
                      <span
                        :id="`popover-mitigation-${categoryIndex}-${rowIndex}`"
                        class="cell-text"
                      >{{ getOptionTitle(row.risk_mitigation_action_id, 'risk_mitigation_action_id') || '—' }}</span>
                      <b-popover
                        v-if="row.risk_mitigation_action_id"
                        :target="`popover-mitigation-${categoryIndex}-${rowIndex}`"
                        triggers="hover"
                        :delay="1000"
                      >
                        <template #title><span>Mitigation Actions in Place</span></template>
                        <span>{{ getOptionTitle(row.risk_mitigation_action_id, 'risk_mitigation_action_id') }}</span>
                      </b-popover>
                    </template>
                  </td>

                  <!-- Treated Likelihood -->
                  <td
                    style="text-align:center;"
                    @click="toggleSelect($event, 'treated_likelihood', categoryIndex + '-' + rowIndex)"
                    :class="{ editing: isEditing(categoryIndex + '-' + rowIndex, 'treated_likelihood') }"
                  >
                    <template v-if="isEditing(categoryIndex + '-' + rowIndex, 'treated_likelihood')">
                      <v-select
                        v-model="row.treated_likelihood"
                        :options="[1,2,3,4,5]"
                        placeholder="1–5"
                        :clearable="true"
                        :searchable="false"
                        :appendToBody="true"
                        class="fixed-select small-number-select"
                        @blur="hideSelect($event, 'treated_likelihood', categoryIndex + '-' + rowIndex)"
                        @input="onFieldChange(row, 'treated_likelihood', $event)"
                      />
                    </template>
                    <template v-else>
                      <span class="cell-text">{{ row.treated_likelihood || '—' }}</span>
                    </template>
                  </td>

                  <!-- Treated Consequence -->
                  <td
                    style="text-align:center;"
                    @click="toggleSelect($event, 'treated_consequence', categoryIndex + '-' + rowIndex)"
                    :class="{ editing: isEditing(categoryIndex + '-' + rowIndex, 'treated_consequence') }"
                  >
                    <template v-if="isEditing(categoryIndex + '-' + rowIndex, 'treated_consequence')">
                      <v-select
                        v-model="row.treated_consequence"
                        :options="[1,2,3,4,5]"
                        placeholder="1–5"
                        :clearable="true"
                        :searchable="false"
                        :appendToBody="true"
                        class="fixed-select small-number-select"
                        @blur="hideSelect($event, 'treated_consequence', categoryIndex + '-' + rowIndex)"
                        @input="onFieldChange(row, 'treated_consequence', $event)"
                      />
                    </template>
                    <template v-else>
                      <span class="cell-text">{{ row.treated_consequence || '—' }}</span>
                    </template>
                  </td>

                  <!-- Residual Risk Rating (calculated) -->
                  <td
                    class="rating-td"
                    :class="getRatingClass(calcRating(row.treated_likelihood, row.treated_consequence))"
                    style="text-align:center; font-weight:bold;"
                  >
                    <span v-if="row.treated_likelihood && row.treated_consequence">
                      {{ calcRating(row.treated_likelihood, row.treated_consequence) }}
                    </span>
                    <span v-else style="color:#aaa;">—</span>
                  </td>

                  <!-- Owner -->
                  <td
                    class="owner-column"
                    @click="toggleSelect($event, 'owner', categoryIndex + '-' + rowIndex)"
                    :class="{ editing: isEditing(categoryIndex + '-' + rowIndex, 'owner') }"
                  >
                    <template v-if="isEditing(categoryIndex + '-' + rowIndex, 'owner')">
                      <treeselect
                        v-model="row.owner_selection"
                        :multiple="true"
                        :options="transformedOwnerOptions"
                        :normalizer="ownerNormalizer"
                        placeholder="Select owner..."
                        :flat="false"
                        :clearable="false"
                        :disable-branch-nodes="true"
                        value-consists-of="LEAF_PRIORITY"
                        :limit="0"
                        :limit-text="(c) => `${c} selected`"
                        @input="handleOwnerSelection($event, row)"
                      >
                        <template #option-label="{ node }">
                          <span :title="node.label" class="truncate-text">{{ node.label }}</span>
                        </template>
                        <template #value-label="{ node }">
                          <span class="truncate-text">{{ node.label }}</span>
                        </template>
                      </treeselect>
                    </template>
                    <template v-else>
                      <span
                        :id="`popover-owner-${categoryIndex}-${rowIndex}`"
                        class="cell-text owner-text"
                      >
                        {{ getOwnerDisplayText(row) }}
                        <b-badge
                          v-if="row.wg_ids && row.wg_ids.length > 0"
                          variant="light-primary"
                          class="wg-badge ml-1"
                          pill
                        >WG</b-badge>
                      </span>
                      <b-popover
                        v-if="getOwnerDisplayText(row) !== '—'"
                        :target="`popover-owner-${categoryIndex}-${rowIndex}`"
                        triggers="hover"
                        :delay="1000"
                      >
                        <template #title><span>Owner</span></template>
                        <span>{{ getOwnerDisplayText(row) }}</span>
                      </b-popover>
                    </template>
                  </td>

                  <!-- Future Recommended Actions -->
                  <td
                    @click="toggleSelect($event, 'future_action', categoryIndex + '-' + rowIndex)"
                    :class="{ editing: isEditing(categoryIndex + '-' + rowIndex, 'future_action') }"
                  >
                    <template v-if="isEditing(categoryIndex + '-' + rowIndex, 'future_action')">
                      <v-select
                        v-model="row.risk_future_action_id"
                        :options="futureActionOptions"
                        placeholder="Select or type future action..."
                        label="title"
                        :reduce="(val) => val.id"
                        :clearable="true"
                        class="fixed-select"
                        :appendToBody="true"
                        :taggable="true"
                        @option:created="onCreateNewField($event, 'risk_future_action', null, categoryIndex + '-' + rowIndex)"
                        @blur="hideSelect($event, 'future_action', categoryIndex + '-' + rowIndex)"
                        @input="onFieldChange(row, 'risk_future_action_id', $event)"
                      >
                        <template #option="option">
                          <span :title="option.title">{{ option.title }}</span>
                        </template>
                      </v-select>
                    </template>
                    <template v-else>
                      <span
                        :id="`popover-future-${categoryIndex}-${rowIndex}`"
                        class="cell-text"
                      >{{ getOptionTitle(row.risk_future_action_id, 'risk_future_action_id') || '—' }}</span>
                      <b-popover
                        v-if="row.risk_future_action_id"
                        :target="`popover-future-${categoryIndex}-${rowIndex}`"
                        triggers="hover"
                        :delay="1000"
                      >
                        <template #title><span>Future Recommended Actions</span></template>
                        <span>{{ getOptionTitle(row.risk_future_action_id, 'risk_future_action_id') }}</span>
                      </b-popover>
                    </template>
                  </td>

                  <!-- Target Completion Date -->
                  <td
                    @click="toggleSelect($event, 'target_date', categoryIndex + '-' + rowIndex)"
                    :class="{ editing: isEditing(categoryIndex + '-' + rowIndex, 'target_date') }"
                  >
                    <template v-if="isEditing(categoryIndex + '-' + rowIndex, 'target_date')">
                      <input
                        type="date"
                        v-model="row.target_completion_date"
                        class="form-control form-control-sm"
                        @blur="onFieldChange(row, 'target_completion_date', row.target_completion_date); hideSelect($event, 'target_date', categoryIndex + '-' + rowIndex)"
                      />
                    </template>
                    <template v-else>
                      <span
                        :id="`popover-date-${categoryIndex}-${rowIndex}`"
                        class="cell-text"
                      >{{ row.target_completion_date ? formatDate(row.target_completion_date) : '—' }}</span>
                      <b-popover
                        v-if="row.target_completion_date"
                        :target="`popover-date-${categoryIndex}-${rowIndex}`"
                        triggers="hover"
                        :delay="1000"
                      >
                        <template #title><span>Target Completion Date</span></template>
                        <span>{{ formatDate(row.target_completion_date) }}</span>
                      </b-popover>
                    </template>
                  </td>

                  <!-- Row Actions -->
                  <td class="text-center action-column">
                    <div class="d-flex flex-column align-items-center justify-content-center">
                      <i
                        v-if="!row.is_archived"
                        class="fas fa-grip-vertical drag-handle mb-25"
                        style="cursor:grab; color:#aaa; font-size:0.85rem;"
                        title="Drag to reorder"
                      ></i>
                      <div class="d-flex flex-wrap justify-content-center" style="gap:2px;">
                        <b-button
                          v-if="$Can('add_risk_tool_item') && !row.is_archived"
                          variant="success"
                          size="sm"
                          @click="addRowToCategory(categoryIndex)"
                          class="p-25"
                          v-b-tooltip.hover
                          title="Add Row"
                        >
                          <i class="fas fa-plus" style="font-size:0.6rem;"></i>
                        </b-button>
                        <b-button
                          v-if="$Can('delete_risk_tool_item') && !row.is_archived"
                          variant="warning"
                          size="sm"
                          @click="archiveRow(categoryIndex, rowIndex)"
                          class="p-25"
                          v-b-tooltip.hover
                          title="Archive Row"
                        >
                          <i class="fas fa-archive" style="font-size:0.6rem;"></i>
                        </b-button>
                        <b-button
                          v-if="row.is_archived"
                          variant="info"
                          size="sm"
                          @click="archiveRow(categoryIndex, rowIndex)"
                          class="p-25"
                          v-b-tooltip.hover
                          title="Unarchive Row"
                        >
                          <i class="fas fa-box-open" style="font-size:0.6rem;"></i>
                        </b-button>
                        <b-button
                          v-if="$Can('delete_risk_tool_item') && !row.is_archived"
                          variant="danger"
                          size="sm"
                          @click="deleteRow(categoryIndex, rowIndex)"
                          class="p-25"
                          v-b-tooltip.hover
                          title="Delete Row"
                        >
                          <i class="fas fa-trash" style="font-size:0.6rem;"></i>
                        </b-button>
                      </div>
                    </div>
                  </td>
                </tr>
              </draggable>
            </template>
          </table>

          <div v-else class="text-center py-3 text-muted">
            <p>No risk data found. Add a new Risk Category to get started.</p>
          </div>
        </div>

        <!-- Add Category Button -->
        <div class="mt-2" v-if="$Can('add_risk_tool_item')">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="addNewCategory"
            :disabled="disableNewCategoryButton"
          >
            <i class="fas fa-plus mr-1"></i> Add Risk Category
          </b-button>
        </div>
      </b-card>
    </b-overlay>

    <!-- ─── Report Modal ─── -->
    <b-modal
      v-model="showReportModalFlag"
      :title="reportModalTitle"
      hide-footer
      size="md"
    >
      <div>
        <b-form-group label="Select Year">
          <v-select
            v-model="reportOptions.year"
            :options="yearOptions"
            placeholder="Select Year"
            :clearable="false"
          />
        </b-form-group>

        <b-form-group label="Filter by Risk Category (optional)">
          <v-select
            v-model="reportOptions.riskCategoryId"
            :options="categoryOptions"
            label="title"
            :reduce="(val) => val.id"
            placeholder="All Risk Categories"
            :clearable="true"
          />
        </b-form-group>

        <b-form-group label="Service Delivery Company Board Chairperson">
          <input
            type="text"
            class="form-control"
            v-model="reportOptions.sdcb_chairperson"
            placeholder="Chairperson name..."
          />
        </b-form-group>

        <b-form-group label="Date Last Monitored by the FARC">
          <input
            type="date"
            class="form-control"
            v-model="reportOptions.farc_date"
          />
        </b-form-group>

        <b-form-group label="Date Last Monitored by Board">
          <input
            type="date"
            class="form-control"
            v-model="reportOptions.board_date"
          />
        </b-form-group>

        <b-form-group v-if="selectedReportType !== 'archived'">
          <b-form-checkbox v-model="reportOptions.includeArchived">
            Include archived risk items
          </b-form-checkbox>
        </b-form-group>

        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="showReportModalFlag = false"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            @click="generateReport"
            :disabled="!reportOptions.year"
          >
            <b-spinner v-if="reportGenerating" small class="mr-1" />
            Create Report
          </b-button>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BOverlay, VBPopover,
  BFormGroup, BFormCheckbox, BSpinner, BModal, BBadge, VBTooltip, BPopover,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import vSelect from "vue-select";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import riskStoreModule from "./riskStoreModule";
import draggable from "vuedraggable";

const STORE_KEY = "riskTool";

export default {
  components: {
    BCard, BRow, BCol, BButton, BOverlay, BFormGroup,
    BFormCheckbox, BSpinner, BModal, BBadge, BPopover,
    vSelect, Treeselect, ToastificationContent, draggable,
  },
  directives: {
    "b-popover": VBPopover,
    "b-tooltip": VBTooltip,
  },

  setup() {
    if (!store.hasModule(STORE_KEY)) {
      store.registerModule(STORE_KEY, riskStoreModule);
    }
    onUnmounted(() => {
      if (store.hasModule(STORE_KEY)) store.unregisterModule(STORE_KEY);
    });
  },

  data() {
    return {
      formShow: false,
      reportGenerating: false,
      showPreviousYear: true,
      showArchived: false,
      isOnSelectChangeLoading: false,
      selectedYear: new Date().getFullYear().toString(),
      yearOptions: Array.from({ length: 6 }, (_, i) => (2024 + i).toString()),

      tableRows: [],

      // Options
      categoryOptions: [],
      riskObjectiveOptions: [],
      riskImpactOptions: [],
      mitigationActionOptions: [],
      futureActionOptions: [],
      ownerOptions: [],
      workingGroupOptions: [],
      crossRegionalOwnerId: null,

      editingCell: {},
      userData: JSON.parse(localStorage.getItem("userData") || "{}"),
      isCreatingNewField: false,

      // Report modal
      showReportModalFlag: false,
      selectedReportType: "regular",
      reportOptions: {
        year: new Date().getFullYear().toString(),
        riskCategoryId: null,
        sdcb_chairperson: null,
        farc_date: null,
        board_date: null,
      },
    };
  },

  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    this.getOptions();
  },

  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },

  computed: {
    groupedRows() {
      return this.tableRows;
    },
    isAdmin() {
      return (
        this.userData?.role === "admin" ||
        this.userData?.role === "supermanager" ||
        this.$Can("show_cib_checklist")
      );
    },
    defaultCompanyId() {
      return this.userData?.company_id ?? null;
    },
    disableNewCategoryButton() {
      // Only disable if there is already an unsaved (blank) category being edited
      return this.tableRows.some((cat) => !cat.risk_category_id);
    },
    reportModalTitle() {
      const titles = {
        regular:  "Risk Register Report",
        summary:  "Overall Rating Summary Report",
        live:     "Live Risks Requiring Attention",
        archived: "Archived Risk Register Report",
      };
      return titles[this.selectedReportType] || "Generate Report";
    },
    optionsLookup() {
      const wgMap = {};
      (this.workingGroupOptions || []).forEach((wg) => { wgMap[wg.id] = wg.group_name; });
      return {
        risk_objective_id:          this.createLookupTable(this.riskObjectiveOptions),
        risk_impact_id:             this.createLookupTable(this.riskImpactOptions),
        risk_mitigation_action_id:  this.createLookupTable(this.mitigationActionOptions),
        risk_future_action_id:      this.createLookupTable(this.futureActionOptions),
        owner_id:                   this.createLookupTable(this.ownerOptions),
        wg_id:                      wgMap,
      };
    },

    transformedOwnerOptions() {
      const crossIds = this.crossRegionalOwnerId ? [this.crossRegionalOwnerId] : [];
      const regular = this.ownerOptions
        .filter((o) => !crossIds.includes(o.id))
        .map((o) => ({ id: o.id, label: o.title, isSelectable: true }));

      if (crossIds.length > 0 && this.workingGroupOptions.length > 0) {
        const crossNodes = this.ownerOptions
          .filter((o) => crossIds.includes(o.id))
          .map((o) => ({
            id: o.id,
            label: o.title,
            children: this.workingGroupOptions.map((wg) => ({
              id: `wg-${wg.id}-${o.id}`,
              label: wg.group_name,
              isWorkingGroup: true,
              isSelectable: true,
              wg_id: wg.id,
              owner_id: o.id,
            })),
          }));
        return [...regular, ...crossNodes];
      }
      return regular;
    },
  },

  methods: {
    // ── Data loading ──────────────────────────────────────────────
    getOptions() {
      return store.dispatch(`${STORE_KEY}/getRiskOptions`).then((res) => {
        if (res.data) {
          this.categoryOptions          = res.data.riskCategories ?? [];
          this.riskObjectiveOptions     = res.data.riskObjectives ?? [];
          this.riskImpactOptions        = res.data.riskImpacts ?? [];
          this.mitigationActionOptions  = res.data.mitigationActions ?? [];
          this.futureActionOptions      = res.data.futureActions ?? [];
          this.ownerOptions             = res.data.owners ?? [];
          this.workingGroupOptions      = res.data.workingGroups ?? [];
          const crossOwner = this.ownerOptions.find(
            (o) => o.title?.trim().toLowerCase() === "cross regional working groups"
          );
          this.crossRegionalOwnerId = crossOwner?.id ?? null;
        }
        this.loadRiskItems();
        return res;
      });
    },

    loadRiskItems() {
      this.formShow = true;
      return store
        .dispatch(`${STORE_KEY}/getRiskItems`, {
          year: this.selectedYear,
          previousYear: this.showPreviousYear,
        })
        .then((res) => {
          this.tableRows = (res.data ?? []).map((cat) => ({
            ...cat,
            rows: cat.rows.map((r) => ({
              ...r,
              owner_selection: this.buildOwnerSelection(r),
            })),
          }));
        })
        .catch((e) => console.error(e))
        .finally(() => { this.formShow = false; });
    },

    onPreviousYearChange() {
      this.loadRiskItems();
    },

    onShowArchivedChange() {
      if (this.showArchived) {
        this.loadArchivedRiskItems();
      } else {
        this.loadRiskItems();
      }
    },

    loadArchivedRiskItems() {
      this.formShow = true;
      return store
        .dispatch(`${STORE_KEY}/getArchivedRiskItems`, { year: this.selectedYear })
        .then((res) => {
          this.tableRows = (res.data ?? []).map((cat) => ({
            ...cat,
            rows: cat.rows.map((r) => ({
              ...r,
              owner_selection: this.buildOwnerSelection(r),
            })),
          }));
        })
        .catch((e) => console.error(e))
        .finally(() => { this.formShow = false; });
    },

    archiveRow(categoryIndex, rowIndex) {
      const row = this.tableRows[categoryIndex]?.rows[rowIndex];
      if (!row || !row.id || String(row.id).startsWith('new_')) return;

      const action = row.is_archived ? 'unarchive' : 'archive';
      this.$bvModal
        .msgBoxConfirm(`Are you sure you want to ${action} this risk?`, {
          title: 'Please Confirm',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: row.is_archived ? 'info' : 'warning',
          okTitle: row.is_archived ? 'Unarchive' : 'Archive',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        })
        .then((confirmed) => {
          if (!confirmed) return;

          store
            .dispatch(`${STORE_KEY}/archiveRiskItem`, row.id)
            .then((res) => {
              row.is_archived = res.data.is_archived;

              // If not in "show archived" mode, remove archived row from view
              if (!this.showArchived && row.is_archived) {
                this.tableRows[categoryIndex].rows.splice(rowIndex, 1);
                if (this.tableRows[categoryIndex].rows.length === 0) {
                  this.tableRows.splice(categoryIndex, 1);
                }
              }
              // If in "show archived" mode, remove unarchived row from view
              if (this.showArchived && !row.is_archived) {
                this.tableRows[categoryIndex].rows.splice(rowIndex, 1);
                if (this.tableRows[categoryIndex].rows.length === 0) {
                  this.tableRows.splice(categoryIndex, 1);
                }
              }

              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Success',
                  text: res.data.message,
                  icon: 'CheckCircleIcon',
                  variant: 'success',
                },
              });
            })
            .catch(() => this.showError(`Failed to ${action} risk item.`));
        });
    },

    // ── Helpers ───────────────────────────────────────────────────
    calcRating(l, c) {
      if (!l || !c) return null;
      return (l * c) + c;
    },

    getRatingClass(rating) {
      if (rating === null || rating === undefined) return "rating-empty";
      if (rating <= 7)  return "rating-green";
      if (rating <= 14) return "rating-orange";
      return "rating-red";
    },

    formatDate(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (isNaN(d)) return val;
      return d.toLocaleDateString("en-IE");
    },

    createLookupTable(arr) {
      const map = {};
      (arr || []).forEach((o) => { map[o.id] = o.title; });
      return map;
    },

    getOptionTitle(id, field) {
      if (!id) return "";
      return this.optionsLookup[field]?.[id] ?? "";
    },

    getCategoryTitle(id) {
      const cat = this.categoryOptions.find((c) => c.id === id);
      return cat?.title ?? "";
    },

    getFilteredCategoryOptions() {
      const usedIds = this.tableRows.map((r) => r.risk_category_id);
      return this.categoryOptions.map((c) => ({
        ...c,
        disabled: usedIds.includes(c.id),
      }));
    },

    getFilteredObjectives(categoryId, currentId) {
      return this.riskObjectiveOptions
        .filter((o) => !o.risk_category_id || o.risk_category_id === categoryId || o.id === currentId)
        .map((o) => ({ ...o }));
    },

    getFilteredImpacts(objectiveId, currentId) {
      return this.riskImpactOptions
        .filter((o) => !o.risk_objective_id || o.risk_objective_id === objectiveId || o.id === currentId)
        .map((o) => ({ ...o }));
    },

    // ── Editing ──────────────────────────────────────────────────
    toggleSelect(event, field, rowIndex) {
      event.stopPropagation();
      this.editingCell = {};
      if (!this.editingCell[rowIndex]) this.$set(this.editingCell, rowIndex, {});
      this.$set(this.editingCell[rowIndex], field, true);

      this.$nextTick(() => {
        const selectContainer = event.target.closest(".fixed-select");
        const dropdown = selectContainer?.querySelector(".vs__dropdown-menu");

        if (selectContainer && dropdown) {
          const containerRect = selectContainer.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - containerRect.bottom;
          const spaceAbove = containerRect.top;
          const dropdownHeight = 200;

          dropdown.classList.remove("dropdown-up", "dropdown-down");

          if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
            dropdown.classList.add("dropdown-up");
          } else {
            dropdown.classList.add("dropdown-down");
          }
        }
      });
    },

    hideSelect(event, field, rowIndex) {
      setTimeout(() => {
        const rel = event.relatedTarget;
        const onDropdown =
          rel &&
          (rel.classList.contains("vs__dropdown-option") ||
            rel.closest?.(".vs__dropdown-menu") ||
            rel.closest?.(".vs__dropdown-toggle"));
        if (!onDropdown) {
          this.editingCell = {};
          if (rowIndex !== undefined) {
            this.$set(this.editingCell, rowIndex, { [field]: false });
          }
        }
      }, 150);
    },

    isEditing(rowIndex, field) {
      if (!this.$Can("update_risk_tool_item")) return false;
      return this.editingCell[rowIndex]?.[field] === true;
    },

    handleClickOutside(event) {
      const clickedTable = event.target.closest(".table-responsive");
      if (!clickedTable) {
        this.editingCell = {};
      }
    },

    // ── Field change & save ───────────────────────────────────────
    onFieldChange(row, field, value) {
      row[field] = value;
      this.saveRow(row);
    },

    onTextareaBlur(row, field) {
      this.editingCell = {};
      // Don't persist an unsaved row just because an empty textarea lost focus
      if (String(row.id || '').startsWith('new_') && !row[field]) return;
      this.saveRow(row);
    },

    onFarReportChange(row) {
      this.saveRow(row);
    },

    saveRow(row) {
      if (!row.id || String(row.id).startsWith("new_")) {
        this.createRow(row);
        return;
      }
      store
        .dispatch(`${STORE_KEY}/updateRiskItem`, {
          id: row.id,
          risk_category_id:           row.risk_category_id,
          risk_objective_id:          row.risk_objective_id,
          risk_impact_id:             row.risk_impact_id,
          likelihood:                 row.likelihood,
          consequence:                row.consequence,
          risk_mitigation_action_id:  row.risk_mitigation_action_id,
          treated_likelihood:         row.treated_likelihood,
          treated_consequence:        row.treated_consequence,
          owner_id:                   row.owner_id,
          owner_ids:                  row.owner_ids ?? [],
          wg_ids:                     row.wg_ids ?? [],
          risk_future_action_id:      row.risk_future_action_id,
          target_completion_date:     row.target_completion_date,
          far_report:                 row.far_report,
          year:                       row.year,
        })
        .catch(() => this.showError("Failed to update risk item."));
    },

    createRow(row) {
      // Don't persist a completely empty row — wait until the user actually enters something
      const hasData =
        row.risk_objective_id ||
        row.risk_impact_id ||
        row.likelihood ||
        row.consequence ||
        row.risk_mitigation_action_id ||
        row.treated_likelihood ||
        row.treated_consequence ||
        row.risk_future_action_id ||
        row.target_completion_date ||
        (row.owner_ids && row.owner_ids.length > 0);
      if (!hasData) return;

      store
        .dispatch(`${STORE_KEY}/createRiskItem`, {
          company_id:                 this.defaultCompanyId,
          risk_category_id:           row.risk_category_id,
          risk_objective_id:          row.risk_objective_id,
          risk_impact_id:             row.risk_impact_id,
          likelihood:                 row.likelihood,
          consequence:                row.consequence,
          risk_mitigation_action_id:  row.risk_mitigation_action_id,
          treated_likelihood:         row.treated_likelihood,
          treated_consequence:        row.treated_consequence,
          owner_id:                   row.owner_id,
          owner_ids:                  row.owner_ids ?? [],
          wg_ids:                     row.wg_ids ?? [],
          risk_future_action_id:      row.risk_future_action_id,
          target_completion_date:     row.target_completion_date,
          far_report:                 row.far_report ?? false,
          year:                       Number(this.selectedYear),
        })
        .then((res) => {
          row.id = res.data?.item?.id ?? row.id;
        })
        .catch(() => this.showError("Failed to create risk item."));
    },

    // ── Create new option (taggable select) ───────────────────────
    onCreateNewField(val, type, parentId, rowKey) {
      if (this.isCreatingNewField) return;
      this.isCreatingNewField = true;
      const titleStr = typeof val === "object" ? val.title || val.label : val;

      const payload = { type, title: titleStr };
      if (type === "risk_objective") payload.risk_category_id = parentId;
      if (type === "risk_impact") payload.risk_objective_id = parentId;

      store
        .dispatch(`${STORE_KEY}/createRiskOption`, payload)
        .then((res) => {
          const newOption = res.data;
          if (type === "risk_category") {
            this.categoryOptions.push(newOption);
            // update row
            const idx = parseInt(rowKey);
            if (this.tableRows[idx]) {
              this.tableRows[idx].risk_category_id = newOption.id;
              this.onCategoryChange(newOption.id, idx);
            }
          } else if (type === "risk_objective") {
            this.riskObjectiveOptions.push(newOption);
            const parts = String(rowKey).split("-");
            const cat = parseInt(parts[0]), row = parseInt(parts[1]);
            if (this.tableRows[cat]?.rows[row]) {
              this.tableRows[cat].rows[row].risk_objective_id = newOption.id;
              this.saveRow(this.tableRows[cat].rows[row]);
            }
          } else if (type === "risk_impact") {
            this.riskImpactOptions.push(newOption);
            const parts = String(rowKey).split("-");
            const cat = parseInt(parts[0]), row = parseInt(parts[1]);
            if (this.tableRows[cat]?.rows[row]) {
              this.tableRows[cat].rows[row].risk_impact_id = newOption.id;
              this.saveRow(this.tableRows[cat].rows[row]);
            }
          } else if (type === "risk_mitigation_action") {
            this.mitigationActionOptions.push(newOption);
            const parts = String(rowKey).split("-");
            const cat = parseInt(parts[0]), row = parseInt(parts[1]);
            if (this.tableRows[cat]?.rows[row]) {
              this.tableRows[cat].rows[row].risk_mitigation_action_id = newOption.id;
              this.saveRow(this.tableRows[cat].rows[row]);
            }
          } else if (type === "risk_future_action") {
            this.futureActionOptions.push(newOption);
            const parts = String(rowKey).split("-");
            const cat = parseInt(parts[0]), row = parseInt(parts[1]);
            if (this.tableRows[cat]?.rows[row]) {
              this.tableRows[cat].rows[row].risk_future_action_id = newOption.id;
              this.saveRow(this.tableRows[cat].rows[row]);
            }
          }
        })
        .catch(() => this.showError(`Failed to create new ${type}.`))
        .finally(() => { this.isCreatingNewField = false; });
    },

    // ── Category change ───────────────────────────────────────────
    onCategoryChange(id, categoryIndex) {
      const cat = this.categoryOptions.find((c) => c.id === id);
      if (cat && this.tableRows[categoryIndex]) {
        this.$set(this.tableRows[categoryIndex], "color", cat.color ?? "#008BCC");
        this.$set(this.tableRows[categoryIndex], "order", cat.order ?? categoryIndex + 1);
        this.$set(this.tableRows[categoryIndex], "risk_category_id", id);

        // Update all rows in this category
        this.tableRows[categoryIndex].rows.forEach((row) => {
          row.risk_category_id = id;
          if (row.id && !String(row.id).startsWith("new_")) {
            this.saveRow(row);
          }
        });
      }
      this.editingCell = {};
    },

    // ── Add/Delete rows & categories ─────────────────────────────
    makeEmptyRow(categoryId) {
      return {
        id: "new_" + Date.now() + Math.random(),
        company_id:                 this.defaultCompanyId,
        risk_category_id:           categoryId,
        risk_objective_id:          null,
        risk_impact_id:             null,
        likelihood:                 null,
        consequence:                null,
        risk_mitigation_action_id:  null,
        treated_likelihood:         null,
        treated_consequence:        null,
        owner_id:                   null,
        owner_ids:                  [],
        wg_ids:                     [],
        owner_selection:            [],
        risk_future_action_id:      null,
        target_completion_date:     null,
        far_report:                 false,
        is_archived:                false,
        year:                       Number(this.selectedYear),
        row_order:                  0,
      };
    },

    addNewCategory() {
      const newCat = {
        risk_category_id: null,
        order:            this.tableRows.length + 1,
        color:            "#008BCC",
        rows:             [this.makeEmptyRow(null)],
      };
      this.tableRows.push(newCat);
    },

    deleteCategory(categoryIndex) {
      this.tableRows.splice(categoryIndex, 1);
    },

    addRowToCategory(categoryIndex) {
      const cat = this.tableRows[categoryIndex];
      const newRow = this.makeEmptyRow(cat.risk_category_id);
      cat.rows.push(newRow);
    },

    deleteRow(categoryIndex, rowIndex) {
      const row = this.tableRows[categoryIndex]?.rows[rowIndex];
      if (!row) return;

      if (row.id && !String(row.id).startsWith("new_")) {
        store
          .dispatch(`${STORE_KEY}/deleteRiskItem`, row.id)
          .then(() => {
            this.tableRows[categoryIndex].rows.splice(rowIndex, 1);
          })
          .catch(() => this.showError("Failed to delete risk item."));
      } else {
        this.tableRows[categoryIndex].rows.splice(rowIndex, 1);
      }
    },

    // ── Drag & Drop ───────────────────────────────────────────────
    onDragEnd(event, categoryIndex) {
      const cat = this.tableRows[categoryIndex];
      if (!cat) return;

      const rows = cat.rows
        .filter((r) => r.id && !String(r.id).startsWith("new_"))
        .map((r, i) => ({ id: r.id, row_order: i + 1 }));

      if (rows.length === 0) return;

      store
        .dispatch(`${STORE_KEY}/updateRiskRowOrder`, {
          risk_category_id: cat.risk_category_id,
          rows,
        })
        .catch(() => this.showError("Failed to update row order."));
    },

    // ── Reports ───────────────────────────────────────────────────
    showReportModal(type) {
      this.selectedReportType = type;
      this.reportOptions = {
        year:             this.selectedYear,
        riskCategoryId:   null,
        sdcb_chairperson: null,
        farc_date:        null,
        board_date:       null,
        includeArchived:  false,
      };
      this.showReportModalFlag = true;
    },

    generateReport() {
      this.reportGenerating = true;
      const payload = {
        type:             this.selectedReportType,
        year:             this.reportOptions.year,
        riskCategoryId:   this.reportOptions.riskCategoryId,
        sdcb_chairperson: this.reportOptions.sdcb_chairperson,
        farc_date:        this.reportOptions.farc_date,
        board_date:       this.reportOptions.board_date,
        includeArchived:  this.reportOptions.includeArchived,
      };

      store
        .dispatch(`${STORE_KEY}/generateRiskReport`, payload)
        .then((response) => {
          if (response.status === 200) {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            const now = new Date();
            const stamp = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
            const names = { regular: "risk-register", summary: "risk-summary", live: "live-risks", archived: "archived-risks" };
            link.setAttribute("download", `${names[this.selectedReportType] ?? "risk-report"}_${stamp}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.showReportModalFlag = false;
          } else {
            this.showError("No data found for the selected filters.");
          }
        })
        .catch(() => this.showError("Failed to generate report."))
        .finally(() => { this.reportGenerating = false; });
    },

    // ── Owner / Working Group helpers ─────────────────────────────
    isCrossRegionalOwner(ownerId) {
      const o = this.ownerOptions.find((x) => Number(x.id) === Number(ownerId));
      return o?.title?.trim().toLowerCase() === "cross regional working groups";
    },

    ownerNormalizer(node) {
      return {
        id:            node.id,
        label:         node.label,
        isWorkingGroup: node.isWorkingGroup,
        children:      node.children,
      };
    },

    buildOwnerSelection(row) {
      const selection = [];
      const ownerIds = row.owner_ids?.length ? row.owner_ids : (row.owner_id ? [row.owner_id] : []);
      const wgIds    = row.wg_ids ?? [];

      ownerIds.forEach((oid) => {
        if (this.isCrossRegionalOwner(oid) && wgIds.length > 0) {
          wgIds.forEach((wgId) => selection.push(`wg-${wgId}-${oid}`));
        } else {
          selection.push(oid);
        }
      });
      return selection;
    },

    handleOwnerSelection(selectedValues, row) {
      const ownerIds = [];
      const wgIds    = [];

      (selectedValues || []).forEach((value) => {
        if (typeof value === "string" && value.startsWith("wg-")) {
          const parts  = value.split("-");   // ["wg", wgId, ownerId]
          const wgId   = Number(parts[1]);
          const ownerId = Number(parts[2]);
          if (!ownerIds.includes(ownerId)) ownerIds.push(ownerId);
          wgIds.push(wgId);
        } else {
          ownerIds.push(Number(value));
        }
      });

      row.owner_ids = ownerIds;
      row.owner_id  = ownerIds[0] ?? null;
      row.wg_ids    = wgIds.length > 0 ? wgIds : [];
      this.saveRow(row);
    },

    getOwnerDisplayText(row) {
      const ownerIds = row.owner_ids?.length ? row.owner_ids : (row.owner_id ? [row.owner_id] : []);
      if (!ownerIds.length) return "—";

      const parts = [];
      ownerIds.forEach((oid) => {
        if (this.isCrossRegionalOwner(oid) && row.wg_ids?.length) {
          row.wg_ids.forEach((wgId) => {
            const name = this.optionsLookup.wg_id?.[wgId];
            if (name) parts.push(name);
          });
        } else {
          const name = this.optionsLookup.owner_id?.[oid];
          if (name) parts.push(name);
        }
      });
      return parts.length ? parts.join(", ") : "—";
    },

    // ── Tooltip helper ────────────────────────────────────────────
    tipHtml(text) {
      if (!text) return '';
      return String(text).replace(/\n/g, '<br>');
    },

    // ── Utilities ─────────────────────────────────────────────────
    showError(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: "Error", text: msg, icon: "AlertCircleIcon", variant: "danger" },
      });
    },
  },
};
</script>

<style scoped>
.risk-tool-table th {
  background-color: #008BCC;
  color: #fff;
  font-size: 0.6rem !important;
  white-space: normal;
  word-wrap: break-word;
  vertical-align: middle;
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 2;
}

.risk-tool-table td {
  font-size: 0.75rem;
  vertical-align: middle;
  padding: 4px 6px;
}

.risk-col { width: 180px; min-width: 150px; }
.impact-col { width: 160px; min-width: 130px; }
.mitigation-col { width: 200px; min-width: 160px; }
.rating-header { background-color: #006a9e !important; }

/* ── Sticky left columns (FAR + Risk No. + Risk) when scrolling horizontally ── */
.risk-tool-table .sticky-col {
  position: sticky;
  z-index: 1;            /* above normal body cells */
  background-color: #fff; /* opaque so scrolled cells don't show through */
}
.risk-tool-table thead .sticky-col {
  z-index: 3;            /* above sticky header (z-index 2) so the corner stays on top */
  background-color: #008BCC;
}

/* Offsets — default (no FAR column, non-admin) */
.risk-tool-table .sticky-no   { left: 0; }
.risk-tool-table .sticky-risk { left: 55px; }

/* Offsets — admin view (FAR column present) */
.risk-tool-table.with-far .sticky-far  { left: 0; }
.risk-tool-table.with-far .sticky-no   { left: 55px; }
.risk-tool-table.with-far .sticky-risk { left: 110px; }

/* Preserve row tints on the frozen body cells */
.far-report-selected .sticky-col { background-color: #fbeaec; }
.archived-row .sticky-col        { background-color: #f5f5f5; }

/* Divider marking the right edge of the frozen pane */
.risk-tool-table .sticky-risk { box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.25); }

.pillar-cell {
  background-color: #008BCC;
  color: #fff;
  font-weight: bold;
  font-size: 0.85rem;
  padding: 8px 12px !important;
  cursor: pointer;
}
.pillar-cell.editing {
  background-color: #006a9e;
}
.pillar-text { font-size: 0.85rem; padding: 4px; }
.pillar-action-column { margin-left: auto; }
.delete-pillar-btn { font-size: 0.65rem; }

.rating-td {
  font-weight: bold;
  font-size: 0.85rem;
  text-align: center;
}
.rating-green  { background-color: #28a745; color: #fff; }
.rating-orange { background-color: #ffc107; color: #333; }
.rating-red    { background-color: #dc3545; color: #fff; }
.rating-empty  { background-color: #f8f9fa; color: #aaa; }

.far-report-selected { background-color: rgba(220, 53, 69, 0.1); }
.far-cell {
  vertical-align: middle !important;
  padding: 4px !important;
  text-align: center !important;
}

.cib-checkbox-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.cib-text {
  position: absolute;
  font-weight: bold;
  color: #dc3545;
  font-size: 0.8rem;
}

.hover-visible { opacity: 0; }

.cib-checkbox-container:hover .cib-text { display: none; }
.cib-checkbox-container:hover .hover-visible { opacity: 1; }

.objective-span-cell { min-width: 150px; }

/* ── Cell text: fixed height so rows don't jump on click ── */
.cell-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  white-space: normal;
  min-height: 40px;
  line-height: 1.4;
  font-size: 0.75rem;
  padding: 2px 0;
  cursor: default;
}
/* textarea-backed cells keep newlines */
.text-wrap { white-space: pre-wrap !important; }

.cell-textarea {
  font-size: 0.75rem;
  min-height: 60px;
  resize: vertical;
}

/* ── v-select toggle: consistent height so cell doesn't jump ── */
.fixed-select { min-width: 120px; }
.fixed-select >>> .vs__dropdown-toggle {
  min-height: 38px !important;
  padding: 2px 24px 2px 6px !important;
  display: flex !important;
  align-items: center !important;
  background: #f8fafc !important;
  border: 1px solid #cbd5e0 !important;
}
.fixed-select >>> .vs__selected-options {
  padding: 0 !important;
}

.small-number-select { min-width: 70px; }
.pillar-select { min-width: 200px; }

.ghost-row { opacity: 0.5; background: #c8ebfb; }
.drag-row { opacity: 0.9; }

.action-column {
  width: 65px;
  min-width: 65px;
  max-width: 65px;
  vertical-align: middle;
  padding: 6px 4px !important;
}

.date-container {
  background: #f8f9fa;
  padding: 12px 15px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.report-menu-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 220px;
}
.trades-font { font-size: 0.75rem; }

.p-25 { padding: 3px 6px !important; }
.mr-25 { margin-right: 3px !important; }

/* ── Owner column ── */
.owner-column {
  width: 220px !important;
  min-width: 220px !important;
  max-width: 220px !important;
}
.owner-text { display: flex; align-items: center; flex-wrap: wrap; gap: 2px; }
.truncate-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px; display: inline-block; }
.wg-badge { font-size: 0.6rem; cursor: default; }

/* ── Treeselect size tweaks inside table cells ── */
.vue-treeselect { font-size: 0.75rem; min-width: 200px; }
.vue-treeselect .vue-treeselect__control { min-height: 38px !important; }
.vue-treeselect .vue-treeselect__placeholder { font-size: 0.75rem; }
.vue-treeselect .vue-treeselect__option { font-size: 0.75rem; }
.vue-treeselect >>> .vue-treeselect__value-container {
  max-height: 60px;
  overflow-y: auto;
}

/* ── Archived rows ── */
.archived-row {
  opacity: 0.55;
  background-color: #f5f5f5 !important;
}
.archived-row td:not(.action-column) .cell-text {
  text-decoration: line-through;
  color: #888 !important;
}
.archived-row td:not(.action-column) {
  color: #888 !important;
}
</style>

<!-- Non-scoped: ensures body-appended dropdowns render above everything -->
<style>
.vs__dropdown-menu {
  z-index: 9999 !important;
}
.vue-treeselect__menu-container {
  z-index: 9999 !important;
}
/* Clip option text to one line so the native title tooltip triggers on hover */
.vs__dropdown-option span {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;
}
</style>
